import { Component, h, Host, Listen, Prop, State } from "@stencil/core";

import { debounce } from "../../utils/debounce";

@Component({
    tag: "ux-iws-product-highlight-slider",
    styleUrl: "product-highlight-slider.scss",
    shadow: false,
})
export class ProductHighlightSlider {
    private baseClass: string = "ux-iws-product-highlight-slider";
    private contentWrapperRef: HTMLDivElement;
    private slider: HTMLUxSliderElement;
    private highlightTeaser: HTMLUxIwsTeaserProductTileElement;
    private breakpoint3PerView: number = 1800;

    /** if set, the big higlight teaser will not be rendered */
    @Prop() noHighlight: boolean = false;

    @State() paginationOffset: number;
    @State() highlightWidth: number;
    @State() sliderContainerHeight: number = null;

    private calculateDimensions = debounce(() => {
        this.highlightWidth =
            this.contentWrapperRef.getBoundingClientRect().width;
        this.paginationOffset =
            this.highlightWidth === 0 ? 0 : this.highlightWidth / 2;
        if (!this.noHighlight) {
            this.sliderContainerHeight = this.slider
                .querySelector(".ux-slider-container")
                .getBoundingClientRect().height;
        }
    }, 100);

    @State() windowWidth = window.innerWidth;
    private lastWindowWidth: number = window.innerWidth;

    private needsSlidesPerViewIncrease = (): boolean => {
        return (
            this.windowWidth >= this.breakpoint3PerView &&
            this.lastWindowWidth <= this.windowWidth &&
            !this.has3SlidesPerView
        );
    };
    private needsSlidesPerViewDecrease = (): boolean => {
        return (
            this.windowWidth < this.breakpoint3PerView &&
            this.has3SlidesPerView &&
            this.lastWindowWidth > this.windowWidth
        );
    };

    @State() has3SlidesPerView: boolean = this.needsSlidesPerViewIncrease();

    private updateSlidesPerView = async () => {
        if (this.noHighlight) return;
        if (this.needsSlidesPerViewIncrease()) {
            await this.slider.updateSlidesPerView(3);
            this.has3SlidesPerView = true;
        } else if (this.needsSlidesPerViewDecrease()) {
            await this.slider.updateSlidesPerView(2);
            this.has3SlidesPerView = false;
        }
    };

    @Listen("resize", { target: "window" })
    async onResize(onComponentLoad = false) {
        if (this.noHighlight) return;
        let needsUpdate = false;
        this.windowWidth = window.innerWidth;
        if (!onComponentLoad) this.windowWidth = window.innerWidth;
        await this.updateSlidesPerView();
        if (this.windowWidth >= 1024 && this.lastWindowWidth < 1024) {
            needsUpdate = true;
        } else if (this.windowWidth < 1024 && this.lastWindowWidth >= 1024) {
            needsUpdate = true;
        }
        this.lastWindowWidth = window.innerWidth;
        if (needsUpdate) {
            setTimeout(async () => {
                await this.slider.update();
                this.calculateDimensions();
            }, 200);
            return;
        }
        this.calculateDimensions();
    }

    async componentDidLoad() {
        if (this.noHighlight) return;
        this.highlightTeaser = this.slider.querySelector(
            "ux-iws-teaser-product-tile"
        );
        await this.onResize();
        await this.slider.removeSlide(0);
        if (this.windowWidth >= this.breakpoint3PerView) {
            await this.slider.update({ slidesPerView: 3 });
            this.has3SlidesPerView = true;
        } else await this.slider.update();
        if (this.noHighlight) return;
        this.checkDimensionsOnFirstRender = true;
    }

    /** the component is rather complex and it needs a dimension check
     * after all the parts of the slider have been rendered for the first time
     */
    @State() checkDimensionsOnFirstRender = false;

    componentDidRender() {
        if (this.checkDimensionsOnFirstRender === true) {
            this.calculateDimensions();
            this.checkDimensionsOnFirstRender = false;
        }
    }

    private slidePrev = async () => {
        await this.slider.slidePrev();
    };

    private slideNext = async () => {
        await this.slider.slideNext();
    };

    private renderHighlightTeaser = () => {
        if (this.slider === undefined) return;

        return (
            <ux-iws-teaser-product-tile
                product-image-url={this.highlightTeaser.productImageUrl}
                product-name={this.highlightTeaser.productName}
                product-desc={this.highlightTeaser.productDesc}
                eyecatcher={this.highlightTeaser.eyecatcher}
            >
                <a
                    href={this.highlightTeaser.linkUrl}
                    class="ux-text-link ux-iws-teaser-product-tile__link"
                >
                    {this.highlightTeaser.linkText}
                    <ux-icon name="arrow-small-right"></ux-icon>
                </a>
            </ux-iws-teaser-product-tile>
        );
    };

    render() {
        const {
            baseClass,
            paginationOffset,
            windowWidth,
            noHighlight,
            has3SlidesPerView,
        } = this;

        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--no-highlight`]: noHighlight,
                }}
            >
                <span
                    class={`${baseClass}__faded-surface ${baseClass}__faded-surface--left`}
                    onClick={this.slidePrev}
                ></span>
                <span
                    class={`${baseClass}__faded-surface ${baseClass}__faded-surface--right`}
                    onClick={this.slideNext}
                ></span>
                <div class={`${baseClass}__wrapper`}>
                    {!noHighlight && (
                        <div
                            class={`${baseClass}__highlight-wrapper`}
                            ref={(el) => (this.contentWrapperRef = el)}
                            style={{
                                height:
                                    this.sliderContainerHeight !== null
                                        ? `${this.sliderContainerHeight}px`
                                        : null,
                            }}
                        >
                            {windowWidth >= 1024 &&
                                this.renderHighlightTeaser()}
                        </div>
                    )}
                    <div class={`${baseClass}__slider-wrapper`}>
                        <ux-slider
                            ref={(el: HTMLUxSliderElement) =>
                                (this.slider = el)
                            }
                            no-arrows
                            breakpoint-s={767}
                            container-width-s={"315px"}
                            space-between-s={10}
                            visible-slides-s={1}
                            breakpoint-m={1023}
                            space-between-m={30}
                            visible-slides-m={2}
                            breakpoint-l={1300}
                            space-between-l={30}
                            visible-slides-l={noHighlight ? 3 : 1}
                            visibleSlidesXl={
                                noHighlight ? 4 : has3SlidesPerView ? 3 : 2
                            }
                            space-between-xl={30}
                            slides-per-column-l={2}
                            slides-per-column-xl={2}
                            reinit-on-resize={noHighlight ? true : null}
                        >
                            {!noHighlight && windowWidth < 1024 && (
                                <ux-slider-slide>
                                    {this.renderHighlightTeaser()}
                                </ux-slider-slide>
                            )}
                            <slot />
                            <ux-slider-pagination
                                slot="pagination"
                                bottom-pagination
                                style={{ left: `-${paginationOffset}px` }}
                            ></ux-slider-pagination>
                        </ux-slider>
                    </div>
                </div>
            </Host>
        );
    }
}
